import { useTranslation } from 'react-i18next';
import type { IEquipmentMaintenanceListEntry } from '@apis/equipmentMaintenance';
import { MaintenanceRow } from '../MaintenanceRow/MaintenanceRow';
import styles from './MaintenanceUpcomingList.module.css';

/** A deadline still waiting to be done. */
export type OpenEntry = IEquipmentMaintenanceListEntry & { nextDueDate: string };

const isOpen = (e: IEquipmentMaintenanceListEntry): e is OpenEntry => e.nextDueDate !== null;

interface IMaintenanceUpcomingListProps {
  entries: IEquipmentMaintenanceListEntry[] | undefined;
  isPending: boolean;
  isError: boolean;
  today: string;
  tz: string;
  /** A row's actions; omitted for a viewer who can't change the schedule. */
  actionsFor?: (entry: OpenEntry) => { onMarkDone: () => void; onEdit: () => void; onDelete: () => void };
}

/** The open deadlines, most urgent first — or why there are none to show. */
export function MaintenanceUpcomingList({ entries, isPending, isError, today, tz, actionsFor }: IMaintenanceUpcomingListProps) {
  const { t } = useTranslation();
  const upcoming = (entries ?? []).filter(isOpen);
  const note = (key: string) => <p className={styles.note}>{t(`equipment.detail.maintenance.${key}`)}</p>;

  if (isPending) return note('loading');
  if (isError) return note('unavailable');
  if (upcoming.length === 0) return note('nothingScheduled');
  // The label only heads a list: a lone note says enough on its own.
  return (
    <>
      <span className={styles.groupLabel}>{t('equipment.detail.maintenance.upcoming')}</span>
      <ul className={styles.list}>
        {upcoming.map((entry) => (
          <MaintenanceRow key={entry.id} entry={entry} today={today} tz={tz} actions={actionsFor?.(entry)} />
        ))}
      </ul>
    </>
  );
}
